/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import cx from 'classnames';

import { Button } from 'sarsaparilla';

import type { ClearButtonTrackingProps, ClickTagProps } from './DatePicker.tracking';
import type { DatePickerCloseAction, DatePickerVariant } from './DatePicker.types';

import styles from './DatePickerContent.module.scss';

const CLOSE_ACTION_LABELS: Record<DatePickerCloseAction, string> = {
    close: 'Close',
    apply: 'Apply Dates',
    remove: 'Apply',
};

export const SINGLE_DATE_CLOSE_ACTION_LABELS: Record<DatePickerCloseAction, string> = {
    close: 'Close',
    apply: 'Apply Date',
    remove: 'Apply',
};

interface DatePickerActionsProps {
    /** What closing would do to the committed value. Sets the primary button's label. */
    closeAction: DatePickerCloseAction;
    /** Primary button label for each close action. Defaults to the plural "Dates" labels. */
    closeActionLabels?: Record<DatePickerCloseAction, string>;
    /** Clears the draft without committing. */
    onReset: () => void;
    isResetDisabled: boolean;
    /** Label for the button that clears the draft. Defaults to the plural "Clear Dates". */
    resetLabel?: string;
    /** Called by the primary action. The wrapper should close itself, which commits the draft. */
    onApply: () => void;
    variant: DatePickerVariant;
    /** Tracking attributes for the clear button. */
    resetTracking?: ClearButtonTrackingProps;
    /** Tracking attributes for the primary button. */
    applyTracking?: ClickTagProps;
}

/**
 * Clear and primary buttons pinned to the bottom of every date picker body.
 */
export function DatePickerActions({
    closeAction,
    closeActionLabels = CLOSE_ACTION_LABELS,
    onReset,
    isResetDisabled,
    resetLabel = 'Clear Dates',
    onApply,
    variant,
    resetTracking,
    applyTracking,
}: DatePickerActionsProps) {
    return (
        <div
            className={cx(styles.ActionButtons, {
                [styles.isDrawer]: variant === 'drawer',
            })}
        >
            <Button
                appearance="subtle"
                onClick={onReset}
                className={styles.ClearButton}
                isDisabled={isResetDisabled}
                {...resetTracking}
            >
                {resetLabel}
            </Button>
            <Button appearance="primary" onClick={onApply} {...applyTracking}>
                {closeActionLabels[closeAction]}
            </Button>
        </div>
    );
}
